/**
 * database_management_controller.js - Database management dev page (Stimulus).
 *
 * Migrated from the loadOnce page script static/js/development/database_management.js.
 * The controller element (#databaseManagementPage, data-controller="database-management")
 * carries the endpoint URLs as data-* attributes. Button clicks stay routed by
 * global-actions.js (data-action="call-fn"/"click-element"/"change-fn"); the
 * handlers those verbs call are exposed on window in connect() and removed in
 * disconnect().
 *
 * Lifecycle: connect() loads the backups list and registers a connect-scoped
 * turbo:before-cache handler that clears the hover-preview tooltip (it is
 * appended to document.body, so without this it would be frozen into the cached
 * page snapshot). disconnect() removes the handler, hides the tooltip, and
 * un-exposes the window handlers. Reference pattern: detail_map_controller.js.
 */

import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
        connect() {
            this.currentTooltip = null;
            this.tooltipTimeout = null;

            // Handlers invoked by global-actions.js call-fn/change-fn delegation.
            // Bound because call-fn calls fn.apply(null, args) (this === null).
            this._exposed = {
                saveToServer: this.saveToServer.bind(this),
                restoreFromServer: this.restoreFromServer.bind(this),
                confirmRestore: this.confirmRestore.bind(this),
                confirmClearDatabase: this.confirmClearDatabase.bind(this),
                restoreFromBackup: this.restoreFromBackup.bind(this),
                deleteBackup: this.deleteBackup.bind(this)
            };
            Object.assign(window, this._exposed);

            this._onBeforeCache = () => this.hideBackupPreview();
            document.addEventListener('turbo:before-cache', this._onBeforeCache);

            this.loadBackupsList();
        }

        disconnect() {
            document.removeEventListener('turbo:before-cache', this._onBeforeCache);
            this.hideBackupPreview();
            Object.keys(this._exposed).forEach((name) => {
                if (window[name] === this._exposed[name]) delete window[name];
            });
        }

        getCSRFToken() {
            const token = document.querySelector('meta[name=csrf-token]');
            return token ? token.getAttribute('content') : '';
        }

        templateUrl(name, value) {
            return this.element.dataset[name].replace('FILENAME', encodeURIComponent(value));
        }

        jsonHeaders() {
            return {
                'Content-Type': 'application/json',
                'X-CSRFToken': this.getCSRFToken()
            };
        }

        parseJsonResponse(response) {
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('application/json')) {
                throw new Error('Server returned non-JSON response. You may need to login again.');
            }
            return response.json();
        }

        saveToServer() {
            fetch(this.element.dataset.saveUrl, {
                method: 'POST',
                headers: this.jsonHeaders()
            })
                .then((r) => this.parseJsonResponse(r))
                .then((data) => {
                    if (data.success) {
                        window.showSuccessMessage(data.message);
                    } else {
                        window.showErrorMessage('Error saving database: ' + data.message);
                    }
                })
                .catch((error) => {
                    window.showErrorMessage('Error saving database: ' + error.message);
                });
        }

        restoreFromServer() {
            if (!confirm('Restore database from server backup? This will REPLACE ALL current tenant data. Are you sure?')) {
                return;
            }

            fetch(this.element.dataset.restoreServerUrl, {
                method: 'POST',
                headers: this.jsonHeaders()
            })
                .then((response) => response.json())
                .then((data) => {
                    if (data.success) {
                        this.handleRestoreSuccess(data);
                    } else {
                        window.showErrorMessage('Error restoring database: ' + data.message);
                    }
                })
                .catch((error) => {
                    window.showErrorMessage('Error restoring database: ' + error.message);
                });
        }

        handleRestoreSuccess(data) {
            let message = data.message;
            if (data.summary) {
                message += ` (clients=${data.summary.clients}, quotes=${data.summary.quotes}, jobs=${data.summary.jobs}, invoices=${data.summary.invoices})`;
            }

            if (data.has_warnings && data.warnings) {
                window.showSuccessMessage(message);
                setTimeout(() => {
                    let warningMessage = 'Restore completed with warnings:\n\n';
                    data.warnings.forEach((warning) => {
                        warningMessage += '* ' + warning + '\n';
                    });
                    alert(warningMessage);
                }, 500);
                setTimeout(() => {
                    window.location.reload();
                }, 3000);
            } else {
                window.showSuccessMessage(message + ' Reloading page...');
                setTimeout(() => {
                    window.location.reload();
                }, 2000);
            }
        }

        confirmRestore() {
            const fileInput = document.getElementById('restoreFileInput');
            const file = fileInput.files[0];
            if (!file) return;

            if (!confirm(`Restore database from "${file.name}"? This will REPLACE ALL current tenant data. Are you sure?`)) {
                fileInput.value = '';
                return;
            }

            document.getElementById('restoreForm').submit();
        }

        confirmClearDatabase() {
            const confirmation1 = confirm('Are you ABSOLUTELY SURE you want to clear the entire database? This will delete ALL data including clients, quotes, jobs, invoices, and settings!');
            if (!confirmation1) return;

            const confirmation2 = confirm('This action is IRREVERSIBLE! Type "DELETE ALL DATA" in the next prompt to confirm.');
            if (!confirmation2) return;

            const userInput = prompt('Type "DELETE ALL DATA" exactly to confirm:');
            if (userInput !== 'DELETE ALL DATA') {
                alert('Confirmation text did not match. Database clear cancelled.');
                return;
            }

            fetch(this.element.dataset.clearUrl, {
                method: 'POST',
                headers: this.jsonHeaders()
            })
                .then((response) => response.json())
                .then((data) => {
                    if (data.success) {
                        window.showSuccessMessage('Database cleared successfully! Reloading page...');
                        setTimeout(() => {
                            window.location.reload();
                        }, 2000);
                    } else {
                        window.showErrorMessage('Error clearing database: ' + data.message);
                    }
                })
                .catch((error) => {
                    window.showErrorMessage('Error clearing database: ' + error.message);
                });
        }

        loadBackupsList() {
            const root = this.element;
            if (!root) return;

            fetch(root.dataset.listBackupsUrl)
                .then((r) => this.parseJsonResponse(r))
                .then((data) => {
                    if (data.success) {
                        this.displayBackupsList(data.backups);
                    } else {
                        window.showErrorMessage('Error loading backups: ' + data.message);
                    }
                })
                .catch((error) => {
                    window.showErrorMessage('Error loading backups: ' + error.message);
                });
        }

        displayBackupsList(backups) {
            const tableBody = document.getElementById('backupsTableBody');
            const table = document.getElementById('backupsTable');
            tableBody.innerHTML = '';

            if (backups.length === 0) {
                tableBody.innerHTML = '<tr><td colspan="4" class="text-center text-muted">No backup files found</td></tr>';
            } else {
                backups.forEach((backup) => {
                    const row = document.createElement('tr');
                    row.className = 'backup-row';
                    row.setAttribute('data-backup-filename', backup.filename);
                    row.innerHTML = this.backupRowHtml(backup);
                    row.addEventListener('mouseenter', (e) => this.showBackupPreview(e, backup.filename));
                    row.addEventListener('mouseleave', () => this.hideBackupPreview());
                    row.addEventListener('mousemove', (e) => this.updateTooltipPosition(e));
                    tableBody.appendChild(row);
                });
            }

            table.style.display = 'block';
        }

        backupRowHtml(backup) {
            let badgeClass = 'bg-secondary';
            if (backup.type === 'Automatic Server Backup') {
                badgeClass = 'bg-primary';
            } else if (backup.type === 'Manual Server Backup') {
                badgeClass = 'bg-success';
            } else if (backup.type === 'Download Backup') {
                badgeClass = 'bg-info';
            } else if (backup.type === 'Pre-Restore Backup') {
                badgeClass = 'bg-warning';
            }

            return `
            <td>
                <span class="badge ${badgeClass}">${backup.type}</span>
            </td>
            <td>${backup.date}</td>
            <td>${backup.size}</td>
            <td>
                <div class="btn-group btn-group-sm" role="group">
                    <button class="btn btn-outline-success" data-action="call-fn" data-fn="restoreFromBackup" data-entity-id="${backup.filename}" title="Restore from this backup">
                        <i class="bi bi-arrow-clockwise"></i> Restore
                    </button>
                    <button class="btn btn-outline-danger" data-action="call-fn" data-fn="deleteBackup" data-entity-id="${backup.filename}" title="Delete this backup">
                        <i class="bi bi-trash"></i> Delete
                    </button>
                </div>
            </td>
        `;
        }

        restoreFromBackup(backupFilename) {
            if (!confirm(`Restore database from backup "${backupFilename}"? This will REPLACE ALL current tenant data. Are you sure?`)) {
                return;
            }

            fetch(this.templateUrl('restoreBackupUrlTemplate', backupFilename), {
                method: 'POST',
                headers: this.jsonHeaders()
            })
                .then((r) => this.parseJsonResponse(r))
                .then((data) => {
                    if (data.success) {
                        this.handleRestoreSuccess(data);
                    } else {
                        window.showErrorMessage('Error restoring backup: ' + data.message);
                    }
                })
                .catch((error) => {
                    window.showErrorMessage('Error restoring backup: ' + error.message);
                });
        }

        deleteBackup(backupFilename) {
            let backupType = 'backup';
            if (backupFilename.startsWith('server_backup_')) {
                backupType = 'automatic server backup';
            } else if (backupFilename.startsWith('manual_backup_')) {
                backupType = 'manual server backup';
            } else if (backupFilename.startsWith('chippie_backup_')) {
                backupType = 'download backup';
            } else if (backupFilename.startsWith('pre_restore_backup_')) {
                backupType = 'pre-restore backup';
            }

            if (!confirm(`Delete ${backupType} "${backupFilename}"? This action cannot be undone!`)) {
                return;
            }

            fetch(this.templateUrl('deleteBackupUrlTemplate', backupFilename), {
                method: 'DELETE',
                headers: this.jsonHeaders()
            })
                .then((r) => this.parseJsonResponse(r))
                .then((data) => {
                    if (data.success) {
                        window.showSuccessMessage(data.message);
                        this.loadBackupsList();
                    } else {
                        window.showErrorMessage('Error deleting backup: ' + data.message);
                    }
                })
                .catch((error) => {
                    window.showErrorMessage('Error deleting backup: ' + error.message);
                });
        }

        showBackupPreview(event, backupFilename) {
            if (this.tooltipTimeout) {
                clearTimeout(this.tooltipTimeout);
            }

            this.tooltipTimeout = setTimeout(() => {
                this.fetchAndShowPreview(event, backupFilename);
            }, 300);
        }

        fetchAndShowPreview(event, backupFilename) {
            this.createTooltip(event, 'Loading preview...', true);

            fetch(this.templateUrl('previewUrlTemplate', backupFilename))
                .then((response) => {
                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }
                    return response.json();
                })
                .then((data) => {
                    if (data.success && data.preview) {
                        const preview = data.preview;
                        const content = `
                        <div class="preview-header">Database Preview</div>
                        <div class="preview-stat"><span class="label">Clients:</span><span class="value">${preview.clients}</span></div>
                        <div class="preview-stat"><span class="label">Quotes:</span><span class="value">${preview.quotes}</span></div>
                        <div class="preview-stat"><span class="label">Jobs:</span><span class="value">${preview.jobs}</span></div>
                        <div class="preview-stat"><span class="label">Invoices:</span><span class="value">${preview.invoices}</span></div>
                        <div class="preview-stat"><span class="label">Backup Size:</span><span class="value">${preview.size}</span></div>
                        <div class="preview-stat"><span class="label">Backup Date:</span><span class="value">${preview.date}</span></div>
                    `;
                        if (this.currentTooltip) this.currentTooltip.innerHTML = content;
                    } else if (this.currentTooltip) {
                        this.currentTooltip.innerHTML = '<div class="preview-header">Preview Error</div><div class="preview-stat"><span class="value">Could not load preview data</span></div>';
                    }
                })
                .catch(() => {
                    if (this.currentTooltip) {
                        this.currentTooltip.innerHTML = '<div class="preview-header">Preview Error</div><div class="preview-stat"><span class="value">Failed to load preview</span></div>';
                    }
                });
        }

        createTooltip(event, content, isLoading) {
            this.hideBackupPreview();

            const tooltip = document.createElement('div');
            tooltip.className = 'backup-preview-tooltip';
            tooltip.innerHTML = isLoading ? `<div class="preview-header">${content}</div>` : content;

            const mouseX = event.pageX || event.clientX + window.pageXOffset;
            const mouseY = event.pageY || event.clientY + window.pageYOffset;
            tooltip.style.left = (mouseX + 5) + 'px';
            tooltip.style.top = (mouseY - 10) + 'px';

            document.body.appendChild(tooltip);
            this.currentTooltip = tooltip;
            this.updateTooltipPosition(event);

            setTimeout(() => {
                tooltip.classList.add('show');
            }, 10);
        }

        updateTooltipPosition(event) {
            if (!this.currentTooltip) return;

            const tooltipRect = this.currentTooltip.getBoundingClientRect();
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            const mouseX = event.pageX || event.clientX + window.pageXOffset;
            const mouseY = event.pageY || event.clientY + window.pageYOffset;

            let left = mouseX + 5;
            let top = mouseY - 10;
            const viewportLeft = left - window.pageXOffset;
            const viewportTop = top - window.pageYOffset;

            if (viewportLeft + tooltipRect.width > viewportWidth - 10) {
                left = mouseX - tooltipRect.width - 5;
            }
            if (viewportLeft < 10) {
                left = window.pageXOffset + 10;
            }
            if (viewportTop < 10) {
                top = mouseY + 15;
            }
            if (viewportTop + tooltipRect.height > viewportHeight - 10) {
                top = mouseY - tooltipRect.height - 5;
            }

            this.currentTooltip.style.left = left + 'px';
            this.currentTooltip.style.top = top + 'px';
        }

        hideBackupPreview() {
            if (this.tooltipTimeout) {
                clearTimeout(this.tooltipTimeout);
                this.tooltipTimeout = null;
            }
            if (this.currentTooltip) {
                this.currentTooltip.remove();
                this.currentTooltip = null;
            }
        }
    }
